import React from 'react';

import styled from '@emotion/styled';

import { FormSubmitButton } from '../../../components/Button/FormSubmitButton';
import { FormComponent } from '../../../components/FormCom/FormComponent';
import { TextInputField } from '../../../components/FormCom/FormField';
import PaystackPaymentModal from '../../../components/PaystackIframe/PaystackIframe';
import useGetLookupCount from '../../../hooks/dashboard/dashboardLoading/useGetLookupCount';
import useGetDriverLookup from '../../../hooks/dashboard/driver-lookup/useGetDriverLookup';
import useLookupConfig from '../../../hooks/dashboard/lookup/useLookupConfig';
import {
  DriverLookupSearchInitialValues,
  DriverLookupSearchSchema,
} from '../../../models/dashboard/driver-lookup/driver-lookup-search';
import { useAppSelector } from '../../../redux/store';
import { DRIVER_LOOKUP_INFO } from '../../../routes/routeConstants';
import Notice from './Notice';

const LookupSearch = () => {
  const { getDriverLookup, loading } = useGetDriverLookup();
  const { getLookupCount } = useGetLookupCount();
  const { LookupConfig } = useLookupConfig();
  const { lookupCountData } = useAppSelector((state) => state.lookupCountData);
  const { authData } = useAppSelector((state) => state.auth);
  const { businessInfo } = useAppSelector((state) => state.businessInfoData);

  const handlePaymentSuccess = async () => {
    await Promise.allSettled([getLookupCount(), LookupConfig()]);
  };

  const noticeMsg = () => {
    if (businessInfo?.isTestMode) {
      return 'You have unlimited lookups in dev mode.';
    }

    if (lookupCountData?.totalAvailableLookupCount === 0) {
      return 'You have run out of look ups. Get access to more.';
    } else {
      if (
        lookupCountData?.availableLookupCount &&
        lookupCountData?.availableLookupCount > 0 &&
        lookupCountData?.freeLookupCount &&
        lookupCountData?.freeLookupCount === 0
      ) {
        return `You have ${lookupCountData?.availableLookupCount} lookup${lookupCountData?.availableLookupCount && lookupCountData?.availableLookupCount > 1 ? 's' : ''}`;
      }
      if (
        lookupCountData?.freeLookupCount &&
        lookupCountData?.freeLookupCount > 0 &&
        lookupCountData?.availableLookupCount &&
        lookupCountData?.availableLookupCount === 0
      ) {
        return `You have ${lookupCountData?.freeLookupCount} free lookup${lookupCountData?.freeLookupCount && lookupCountData?.freeLookupCount > 1 ? 's' : ''}.`;
      }

      return `You have ${lookupCountData?.availableLookupCount} lookup${lookupCountData?.availableLookupCount && lookupCountData?.availableLookupCount > 1 ? 's' : ''} and ${lookupCountData?.freeLookupCount} free lookup${lookupCountData?.freeLookupCount && lookupCountData?.freeLookupCount > 1 ? 's' : ''}.`;
    }
  };

  const onSubmit = (value: any, actions: any) => {
    getDriverLookup(value?.ninNumber, DRIVER_LOOKUP_INFO, () => {
      actions.resetForm(DriverLookupSearchInitialValues);
      getLookupCount();
    });
  };

  return (
    <>
      <Container>
        <Header>Driver Performance Search</Header>
        <Description>Enter driver NIN to view performance details</Description>
        <FormComponent
          initialValues={DriverLookupSearchInitialValues}
          schema={DriverLookupSearchSchema}
          onSubmit={onSubmit}
        >
          <FormRow>
            <TextInputField
              name='ninNumber'
              id='ninNumber'
              type='text'
              placeholder='Enter driver NIN...'
            />
            <FormSubmitButton
              loading={loading}
              disabled={
                !businessInfo?.isTestMode &&
                !authData?.user?.isWithinFreeTrial &&
                lookupCountData?.totalAvailableLookupCount === 0
              }
            >
              Proceed
            </FormSubmitButton>
          </FormRow>
        </FormComponent>

        {!authData?.user?.isWithinFreeTrial && (
          <Notice variant={'info'} message={noticeMsg() ?? ''} />
        )}
      </Container>

      <PaystackPaymentModal onSuccess={handlePaymentSuccess} />
    </>
  );
};

export default LookupSearch;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 765px;
  border-radius: 5px;
  background-color: #fff;
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 18px;
  color: ${({ theme }) => theme.palette.logirateBlack};
`;

const Description = styled.div`
  font-size: 14px;
  line-height: 14px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;
